import React, { useState, useEffect } from 'react';
import { Container, Title, Button, Skeleton, Loader } from "@bsf/force-ui";
import { LoaderCircle } from 'lucide-react';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import PostWidgetItem from '@components/Dashboard/PostWidgetItem';
import toast, { Toaster } from 'react-hot-toast';

const PostSkins = () => {
    const [allWidgetsData, setAllWidgetsData] = useState(null);
    const [loadingActivate, setLoadingActivate] = useState(false);
    const [loadingDeactivate, setLoadingDeactivate] = useState(false);
    const [updateCounter, setUpdateCounter] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = () => {
            setLoading(true);
            apiFetch({
                path: '/uael/v1/postskins',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': uaelSettingsData.uael_nonce_action, // Use the correct nonce
                },
            })
                .then((data) => {
                    const widgetsData = convertToWidgetsArray(data);
                    setAllWidgetsData(widgetsData);
                    setLoading(false); // Stop loading
                })
                .catch((err) => {
                    setLoading(false); // Stop loading
                });
        };

        fetchSettings();
    }, []);

    const handleActivateAll = async () => {
        setLoadingActivate(true);

        const formData = new window.FormData();
        formData.append('action', 'uael_bulk_activate_skins');
        formData.append('nonce', uaelSettingsData.ajax_nonce);

        apiFetch({
            url: uaelSettingsData.ajax_url,
            method: 'POST',
            body: formData,
        }).then((data) => {
            setLoadingActivate(false);
            if (data.success) {
                setAllWidgetsData(prevWidgets =>
                    prevWidgets.map(widget => ({ ...widget, is_active: true }))
                );
                setUpdateCounter(prev => prev + 1)
                toast.success(__('All skins activated successfully!.', 'uael'));
            } else if (data.error) {
                console.error('AJAX request failed:', data.error);
                toast.error(__('Failed to activate all skins.', 'uael'));
            }
        }).catch((error) => {
            setLoadingActivate(false);
            console.error('Error during AJAX request:', error);
            toast.error(__('An error occurred while activating all skins.', 'uael'));
        });
    };

    const handleDeactivateAll = async () => {
        setLoadingDeactivate(true);

        const formData = new window.FormData();
        formData.append('action', 'uael_bulk_deactivate_skins');
        formData.append('nonce', uaelSettingsData.ajax_nonce);

        apiFetch({
            url: uaelSettingsData.ajax_url,
            method: 'POST',
            body: formData,
        }).then((data) => {
            setLoadingDeactivate(false);
            if (data.success) {
                setAllWidgetsData(prevWidgets =>
                    prevWidgets.map(widget => ({ ...widget, is_active: false }))
                );
                setUpdateCounter(prev => prev + 1);
                toast.success(__('All skins deactivated successfully!.', 'uael'));
            } else if (data.error) {
                console.error('AJAX request failed:', data.error);
                toast.error(__('Failed to deactivate all skins.', 'uael'));
            }
        }).catch((error) => {
            setLoadingDeactivate(false);
            console.error('Error during AJAX request:', error);
            toast.error(__('An error occurred while deactivating all skins.', 'uael'));
        });
    };

    function convertToWidgetsArray(data) {
        const widgets = [];

        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const widget = data[key];
                widgets.push({
                    id: key,
                    slug: widget.slug,
                    title: widget.title,
                    image: widget.image,
                    default: widget.default,
                    is_active: widget.is_activate !== undefined ? widget.is_activate : true,
                });
            }
        }

        return widgets;
    }

    if (loading) {
        return (
            <div className='h-screen w-screen flex items-center justify-center' style={{ position: 'relative' }}>
                <Loader icon={null} size="lg" className="bg-transparent relative uae-loader-icon" />
            </div>
        );
    }

    return (
        <>
            <Toaster
                position="top-right"
                reverseOrder={false}
                gutter={8}
                containerStyle={{
                    top: 20,
                    right: 20,
                    marginTop: '40px',
                }}
                toastOptions={{
                    duration: 5000,
                    style: {
                        background: 'white',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#6005ff',
                            secondary: '#fff',
                        },
                    },
                }}
            />
            <Title
                description=""
                icon={null}
                iconPosition="right"
                size="sm"
                tag="h2"
                title={__("Post Widget Skins", "uael")}
            />
            <div className='rounded-lg bg-white mb-4'>
                <div className='flex flex-col md:flex-row items-center justify-between p-4' 
                style={{
                    paddingBottom: '0',
                    marginTop: '16px',
                }}>
                    <p className='m-0 text-sm font-semibold text-text-primary'>Skins</p>
                    <div className='flex items-center gap-x-2 mr-7'>
                        <Button
                            icon={loadingActivate ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            variant="outline"
                            className="uae-bulk-action-button"
                            onClick={handleActivateAll}
                        >
                            {loadingActivate ? 'Activating...' : 'Activate All'}
                        </Button>
                        <Button
                            icon={loadingDeactivate ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            variant="outline"
                            onClick={handleDeactivateAll}
                            className="uae-bulk-action-button"
                        >
                            {loadingDeactivate ? 'Deactivating...' : 'Deactivate All'}
                        </Button>
                    </div>
                </div>
                <div className='flex bg-black flex-col rounded-lg p-4'>
                    {loading ? (
                        <Container
                            align="stretch"
                            className="p-2 gap-1.5"
                            cols={1}
                            style={{
                                backgroundColor: "#F9FAFB", height: '380px'
                            }}
                            containerType="grid"
                            gap=""
                            justify="start"
                        >
                            {[...Array(4)].map((_, index) => (
                                <Container.Item
                                    key={index}
                                    alignSelf="auto"
                                    className="text-wrap rounded-md bg-background-primary p-6 space-y-2"
                                >
                                    <Skeleton className='w-12 h-2 rounded-md' />
                                    <Skeleton className='w-16 h-2 rounded-md' />
                                    <Skeleton className='w-12 h-2 rounded-md' />
                                </Container.Item>
                            ))}
                        </Container>
                    ) : (
                        <Container
                            align="stretch"
                            className="p-2 gap-1.5 grid grid-cols-1 md:grid-cols-2"
                            containerType="grid"
                            gap=""
                            justify="start"
                            style={{ backgroundColor: '#F9FAFB' }}
                        >
                            {allWidgetsData?.slice(0, 4).map((widget) => (
                                <Container.Item
                                    key={widget.id}
                                    alignSelf="auto"
                                    className="text-wrap rounded-md shadow-container-item bg-background-primary p-4"
                                >
                                    <PostWidgetItem widget={{ ...widget, updateCounter }} key={widget.id} updateCounter={updateCounter} />
                                </Container.Item>
                            ))}
                        </Container>
                    )}
                </div>
            </div>
        </>
    );
}

export default PostSkins;
